import { useCallback, useEffect, useMemo, useState } from 'react'
import { Scissors, Loader2 } from 'lucide-react'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportServiceMixReport,
  fetchServiceMixReport,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

const CHART_COLORS = [
  'hsl(var(--primary))',
  '#D4A373',
  '#7C9EB2',
  '#9B8EC4',
  '#6B9E78',
  '#C97B84',
]

export function ServiceMixReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchServiceMixReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load service mix report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportServiceMixReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const chartData = useMemo(() => {
    if (!report?.by_category) return []
    return report.by_category
      .filter((row) => Number(row.amount) > 0)
      .map((row) => ({
        name: row.category_name,
        value: Number(row.amount),
        share: row.share_pct,
      }))
  }, [report])

  const hasActivity = Number(report?.summary?.gross_total ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Scissors className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Service category mix</h2>
            <p className="text-sm text-muted-foreground">
              Revenue share by service category and top-performing services from completed POS sales.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-4">
            <SummaryCard
              label="Service revenue"
              value={`AED ${formatReportMoney(report.summary.gross_total)}`}
            />
            <SummaryCard label="Lines sold" value={String(report.summary.line_count)} />
            <SummaryCard label="Total qty" value={String(report.summary.quantity)} />
            <SummaryCard label="Unique services" value={String(report.summary.unique_services)} />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: AED {formatReportMoney(report.compare_summary.gross_total)} ·{' '}
              {report.compare_summary.line_count} lines · {report.compare_summary.quantity} qty
            </p>
          ) : null}

          {hasActivity && chartData.length > 0 ? (
            <div className="mb-6 rounded-2xl border border-border/70 bg-muted/10 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Mix by category
              </p>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={176}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`AED ${formatReportMoney(value)}`, 'Amount']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Lines</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Share</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No service sales recorded in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_category.map((row) => (
                    <tr key={row.category_id ?? row.category_name} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{row.category_name}</td>
                      <td className="px-4 py-3">{row.line_count}</td>
                      <td className="px-4 py-3">{row.quantity}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                      <td className="px-4 py-3">{row.share_pct}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Top services
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Service</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Share</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity || report.top_services.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No service sales recorded in this period.
                    </td>
                  </tr>
                ) : (
                  report.top_services.map((row) => (
                    <tr key={row.service_id ?? row.service_name} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{row.service_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.category_name ?? '—'}</td>
                      <td className="px-4 py-3">{row.quantity}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                      <td className="px-4 py-3">{row.share_pct}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
